import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent } from "@/components/ui/card"
import { GlassCard } from "./GlassCard"
import { LoadingSpinner } from "./LoadingSpinner"
import { 
  X, 
  Heart, 
  ShoppingBag, 
  Lock, 
  Crown,
  Star,
  Truck,
  Shield,
  RotateCcw,
  Zap
} from "lucide-react"

interface ProductDetailModalProps {
  isOpen: boolean
  onClose: () => void
  product?: {
    id: string
    name: string
    price: number
    images: string[]
    description: string
    features: string[]
    sizes: string[]
    colors: string[]
    isApexLocked?: boolean
    isLimited?: boolean
    stockCount?: number
    tier: 'initiate' | 'creator' | 'architect'
    materials: string[]
    careInstructions: string[]
    userTier: 'initiate' | 'creator' | 'architect'
  }
}

const mockProduct = {
  id: "1",
  name: "Apex Kinetic Joggers",
  price: 180,
  images: [
    "https://images.unsplash.com/photo-1556905055-8f358a7a47b2?w=500&h=600&fit=crop",
    "https://images.unsplash.com/photo-1594633312681-425c7b97ccd1?w=500&h=600&fit=crop",
    "https://images.unsplash.com/photo-1506629905607-d7193157bab8?w=500&h=600&fit=crop"
  ],
  description: "Engineered for movement, crafted for luxury. These joggers blend technical performance with architectural elegance.",
  features: [
    "4-way stretch technical fabric",
    "Water-resistant finish", 
    "Zippered security pockets",
    "Reinforced stress points",
    "Tapered athletic fit"
  ],
  sizes: ["XS", "S", "M", "L", "XL", "XXL"],
  colors: ["Charcoal", "Sage", "Stone"],
  isApexLocked: true,
  isLimited: true,
  stockCount: 12,
  tier: "creator" as const,
  materials: ["87% Nylon", "13% Elastane"],
  careInstructions: ["Machine wash cold", "Hang dry", "Do not bleach"],
  userTier: "creator" as const
}

export function ProductDetailModal({ 
  isOpen, 
  onClose, 
  product = mockProduct 
}: ProductDetailModalProps) {
  const [selectedImage, setSelectedImage] = useState(0)
  const [selectedSize, setSelectedSize] = useState("")
  const [selectedColor, setSelectedColor] = useState(product.colors[0])
  const [isWishlisted, setIsWishlisted] = useState(false)
  const [isAddingToCart, setIsAddingToCart] = useState(false)

  if (!isOpen) return null

  const canPurchase = product.userTier === 'architect' || !product.isApexLocked
  const isOutOfStock = product.stockCount === 0

  const handleAddToCart = async () => {
    if (!selectedSize) return
    
    setIsAddingToCart(true)
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500))
    setIsAddingToCart(false)
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm animate-fade-in">
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="w-full max-w-4xl max-h-[90vh] overflow-hidden">
          <GlassCard className="relative" blur="lg" opacity="high">
            {/* Close Button */}
            <Button
              variant="ghost"
              size="sm"
              className="absolute top-4 right-4 z-10 bg-black/50 hover:bg-black/70"
              onClick={onClose}
            >
              <X size={20} />
            </Button>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-0 h-full">
              {/* Image Gallery */}
              <div className="relative">
                <div className="aspect-square overflow-hidden">
                  <img
                    src={product.images[selectedImage]}
                    alt={product.name}
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                  />
                </div>
                
                {/* Image Thumbnails */}
                <div className="absolute bottom-4 left-4 flex gap-2">
                  {product.images.map((image, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImage(index)}
                      className={`
                        w-16 h-16 rounded-lg overflow-hidden border-2 transition-all
                        ${selectedImage === index 
                          ? 'border-primary scale-110' 
                          : 'border-white/30 hover:border-white/50'
                        }
                      `}
                    >
                      <img
                        src={image}
                        alt={`${product.name} view ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>

                {/* Badges */}
                <div className="absolute top-4 left-4 flex flex-col gap-2">
                  {product.isApexLocked && (
                    <Badge className="bg-primary text-black">
                      <Lock size={12} className="mr-1" />
                      Apex Only
                    </Badge>
                  )}
                  {product.isLimited && (
                    <Badge className="bg-accent text-accent-foreground animate-pulse-gold">
                      <Crown size={12} className="mr-1" />
                      Limited Edition
                    </Badge>
                  )}
                </div>
              </div>

              {/* Product Details */}
              <div className="p-8 flex flex-col h-full max-h-[90vh] overflow-y-auto">
                <div className="space-y-6 flex-1">
                  {/* Header */}
                  <div>
                    <h1 className="text-3xl font-bold text-foreground mb-2">
                      {product.name}
                    </h1>
                    <div className="flex items-center justify-between">
                      <p className="text-3xl font-bold text-primary">
                        ${product.price}
                      </p>
                      <button
                        onClick={() => setIsWishlisted(!isWishlisted)}
                        className="p-2 rounded-full hover:bg-white/10 transition-colors"
                      >
                        <Heart 
                          size={24} 
                          className={isWishlisted ? "fill-red-400 text-red-400" : ""} 
                        />
                      </button>
                    </div>
                  </div>

                  {/* Stock Status */}
                  {product.stockCount && product.stockCount > 0 && (
                    <div className="flex items-center gap-2 text-sm">
                      <div className="w-2 h-2 bg-accent rounded-full"></div>
                      <span className="text-accent font-medium">
                        {product.stockCount} left in stock
                      </span>
                    </div>
                  )}

                  {/* Description */}
                  <p className="text-muted-foreground leading-relaxed">
                    {product.description}
                  </p>

                  {/* Features */}
                  <div>
                    <h3 className="font-semibold text-foreground mb-3">Key Features</h3>
                    <ul className="space-y-2">
                      {product.features.map((feature, index) => (
                        <li key={index} className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Star size={12} className="text-primary flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Color Selection */}
                  <div>
                    <h3 className="font-semibold text-foreground mb-3">
                      Color: {selectedColor}
                    </h3>
                    <div className="flex gap-2">
                      {product.colors.map(color => (
                        <button
                          key={color}
                          onClick={() => setSelectedColor(color)}
                          className={`
                            px-4 py-2 rounded-lg border transition-all
                            ${selectedColor === color
                              ? 'border-primary bg-primary/10 text-primary'
                              : 'border-white/30 hover:border-white/50'
                            }
                          `}
                        >
                          {color}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Size Selection */}
                  <div>
                    <h3 className="font-semibold text-foreground mb-3">Size</h3>
                    <div className="grid grid-cols-3 gap-2">
                      {product.sizes.map(size => (
                        <button
                          key={size}
                          onClick={() => setSelectedSize(size)}
                          className={`
                            py-3 rounded-lg border transition-all font-medium
                            ${selectedSize === size
                              ? 'border-primary bg-primary/10 text-primary'
                              : 'border-white/30 hover:border-white/50'
                            }
                          `}
                        >
                          {size}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Purchase Section */}
                  <div className="space-y-4 pt-4 border-t border-white/10">
                    {!canPurchase && (
                      <Card className="border-primary/50 bg-primary/5">
                        <CardContent className="p-4 text-center">
                          <Crown className="mx-auto mb-2 text-primary" size={24} />
                          <p className="text-sm text-foreground font-medium">
                            Upgrade to Architect tier to access this exclusive item
                          </p>
                        </CardContent>
                      </Card>
                    )}

                    <div className="flex gap-3">
                      <Button
                        onClick={handleAddToCart}
                        disabled={!canPurchase || !selectedSize || isOutOfStock || isAddingToCart}
                        className="flex-1 bg-primary hover:bg-primary/90 text-black font-semibold h-12"
                      >
                        {isAddingToCart ? (
                          <LoadingSpinner size="sm" variant="muted" />
                        ) : (
                          <>
                            <ShoppingBag size={20} className="mr-2" />
                            {isOutOfStock ? 'Out of Stock' : 'Add to Cart'}
                          </>
                        )}
                      </Button>
                      
                      <Button
                        variant="outline"
                        className="px-6 h-12"
                        disabled={!canPurchase || isOutOfStock}
                      >
                        <Zap size={20} />
                      </Button>
                    </div>

                    {/* Benefits */}
                    <div className="grid grid-cols-3 gap-4 pt-4 text-center">
                      <div className="flex flex-col items-center gap-1">
                        <Truck size={16} className="text-accent" />
                        <span className="text-xs text-muted-foreground">Free Shipping</span>
                      </div>
                      <div className="flex flex-col items-center gap-1">
                        <RotateCcw size={16} className="text-accent" />
                        <span className="text-xs text-muted-foreground">30-Day Returns</span>
                      </div>
                      <div className="flex flex-col items-center gap-1">
                        <Shield size={16} className="text-accent" />
                        <span className="text-xs text-muted-foreground">Lifetime Warranty</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </GlassCard>
        </div>
      </div>
    </div>
  )
}